import type {Poster} from '@PosterWhiteboard/poster/poster.class';
import {CommonMethods} from '@PosterWhiteboard/common-methods';
import {calculateAdjustedModulus, easeInOutCubic, getScaleToFit, roundPrecision, SCALE_PRECISION} from '@Utils/math.util';
import {WhiteboardDomElement} from '@PosterWhiteboard/poster/whiteboard-dom.types';
import {isIOS} from 'react-device-detect';
import {PosterModeType} from '@PosterWhiteboard/poster/poster-mode.class';
import {debounce} from '@Utils/misc.util';
import {isMouseWheelEvent} from '@Utils/dom.util';
import {getPosterUi} from '@PosterWhiteboard/ui/poster-ui';
import {PosterGestures} from '@PosterWhiteboard/poster/poster-gestures.class';
import {PAGE_OPTIONS_WIDTH, PAGE_SLIDER_CONTAINER_HEIGHT} from '@PosterWhiteboard/page/page.types';
import {isEditorMobileVariant} from '@Libraries/is-editor-mobile-variant';
import {CanvasZoomSource} from '@Libraries/ga-events';
import {createWheelZoomGestureTracker, trackCanvasZoom} from '@Libraries/poster-editor-tracking.library';

export interface PosterScalingObject {
  scale: number;
  isScaleAnimating: boolean;
  isCurrentScaleBestFit: boolean;
}

interface ZoomData {
  bestFit: boolean;
  bestFitScale: number;
  currentScale: number;
}

export const ZOOM_MAX = isIOS ? 2 : 4;
export const ZOOM_MIN = 0.1;
const FIT_TO_SCREEN_BUFFER_DESKTOP = 40;
const FIT_TO_SCREEN_BUFFER_MOBILE = 0;

class PosterScaling extends CommonMethods {
  private readonly poster: Poster;
  // The resize/fullscreen callbacks are debounced, so they can fire AFTER dispose() has
  // disconnected the observers — against a torn-down editor DOM. Guard them out.
  private isDisposed = false;
  public isFitToScreen = false;
  public isScaleAnimating = false;
  public animatingScaleEndValue?: number;
  private resizeObserverWhiteboardContainer;
  private resizeObserverPostPosterElement;
  private animateZoomFrameId?: number;
  private gestures?: PosterGestures;
  private readonly boundOnFullscreenChange: () => void;
  private readonly boundOnWindowResize: () => void;
  private readonly debouncedAnimatedContainerResize: () => void;

  constructor(poster: Poster) {
    super();
    this.poster = poster;
    this.boundOnFullscreenChange = this.onWindowResize.bind(this);
    this.boundOnWindowResize = debounce(this.onWindowResize.bind(this), 100);
    this.debouncedAnimatedContainerResize = debounce(() => {
      this.onWhitboardContainerResized(true);
    }, 100);
    document.addEventListener('fullscreenchange', this.boundOnFullscreenChange);
    window.addEventListener('resize', this.boundOnWindowResize);
    const postPosterElement = window.posterEditor?.elements[WhiteboardDomElement.POST_POSTER_ELEMENT]?.get(0);
    const whiteboardContainerElement = window.posterEditor?.elements[WhiteboardDomElement.WHITEBOARD_CONTAINER]?.get(0);
    if (!whiteboardContainerElement || !postPosterElement) {
      throw new Error('Whiteboard container element not defined');
    }
    this.resizeObserverWhiteboardContainer = new ResizeObserver(this.handleWhiteboardContainerResize.bind(this));
    this.resizeObserverWhiteboardContainer.observe(whiteboardContainerElement);

    this.resizeObserverPostPosterElement = new ResizeObserver(debounce(this.refreshFitToScreen.bind(this), 100));
    this.resizeObserverPostPosterElement.observe(postPosterElement);

    this.initInteractGestures(whiteboardContainerElement);
  }

  public toObject(): PosterScalingObject {
    return {
      scale: this.scale,
      isScaleAnimating: this.isScaleAnimating,
      isCurrentScaleBestFit: this.isCurrentScaleBestFit(),
    };
  }

  public dispose(): void {
    this.isDisposed = true;
    document.removeEventListener('fullscreenchange', this.boundOnFullscreenChange);
    window.removeEventListener('resize', this.boundOnWindowResize);
    if (this.animateZoomFrameId) {
      window.clearTimeout(this.animateZoomFrameId);
      this.animateZoomFrameId = undefined;
    }
    this.gestures?.dispose();
    this.resizeObserverWhiteboardContainer.disconnect();
    this.resizeObserverPostPosterElement.disconnect();
  }

  private initInteractGestures(whiteboardContainerElement: HTMLElement): void {
    if (!PosterGestures.shouldEnableGestures()) {
      return;
    }

    this.gestures = new PosterGestures(this.poster);
    this.gestures.initInteractGestures(whiteboardContainerElement);
  }

  /**
   * On mobile the container height is CSS-animated (panel push, keyboard) so the canvas must track it
   * per ResizeObserver tick — fitting instantly keeps the canvas in lockstep with the panel. Desktop
   * size changes are discrete, so they keep the debounced, eased fit.
   */
  private handleWhiteboardContainerResize(): void {
    if (isEditorMobileVariant()) {
      this.onWhitboardContainerResized(false);
      return;
    }
    this.debouncedAnimatedContainerResize();
  }

  public onWhitboardContainerResized(animate = true): void {
    if (this.isDisposed) {
      return;
    }
    if (window.PMW.redux.store.getState().posterEditor.fitToScreenOnWhiteboardResize) {
      this.poster.redux.updateFitToScreenOnWhiteboardResize(false);
      this.fitToScreen(animate);
    } else if (this.isFitToScreen) {
      this.fitToScreen(animate);
    }

    if (!this.isScaleAnimating) {
      this.scrollToPendingPage();
    }
  }

  /**
   * Scrolls to the page a timeline toggle recorded for this resize. Only meaningful once the pages
   * have their final size: a scroll issued while the fit-to-screen animation is still growing them
   * is clamped by the too-short content and lost, so mid-animation callers leave the page id for
   * the animation's completion to consume.
   */
  private scrollToPendingPage(): void {
    const pageIdToScrollTo = window.PMW.redux.store.getState().posterEditor.pageIdToScrollToOnWhiteboardResize;
    if (!pageIdToScrollTo) {
      return;
    }
    this.poster.redux.updatePageIdToScrollToOnWhiteboardResize(undefined);
    getPosterUi().scrollToPage(pageIdToScrollTo, false);
  }

  private refreshFitToScreen(): void {
    if (this.isDisposed) {
      return;
    }
    if (this.isFitToScreen) {
      this.fitToScreen(true);
    }
  }

  public fitToScreen(animate = false, duration = 100): void {
    this.setZoom(this.getFitToScreenScale(), animate, true, duration);
  }

  public zoomOut(): void {
    const data = this.getZoomData();
    const zoomFactor = this.getZoomOutStepFromCurrentScale(data.currentScale);

    if (data.bestFitScale < data.currentScale && data.bestFitScale > data.currentScale - zoomFactor) {
      this.fitToScreen();
    } else {
      this.setZoom(data.currentScale - zoomFactor);
    }
  }

  get scale(): number {
    if (this.animatingScaleEndValue !== undefined) {
      return this.animatingScaleEndValue;
    }

    try {
      const page = this.poster.getCurrentPage();
      // Layout width, not the bounding rect: inactive pages are CSS-scaled, and a scaled rect would
      // read as a zoom that never matches fit-to-screen and re-fit forever.
      const measured = parseFloat(window.getComputedStyle(page.canvasBackdrop).width) / this.poster.width;
      if (measured > 0 && Number.isFinite(measured)) {
        return measured;
      }
      return page.fabricCanvas.getZoom();
    } catch {
      return 1;
    }
  }

  private readonly wheelZoomGestureTracker = createWheelZoomGestureTracker((direction): void => {
    trackCanvasZoom(CanvasZoomSource.WHEEL_OR_PINCH, direction);
  });

  public zoomOnWheelEvent = (e: WheelEvent): void => {
    if (e.ctrlKey && this.scale && window.visualViewport?.scale === 1) {
      e.preventDefault();

      const currentScale = this.scale;
      const baseMultiplier = isMouseWheelEvent(e) ? 1.5 : 1.0;
      const scaleSensitivity = Math.max(1, currentScale * 0.8); // Increases with zoom
      const sensitivity = (150 * baseMultiplier) / scaleSensitivity;

      const zoomFactor = 1 + Math.abs(e.deltaY) / sensitivity;
      const newScale = e.deltaY > 0 ? this.validateNewZoom(currentScale / zoomFactor) : this.validateNewZoom(currentScale * zoomFactor);

      const whiteboardContainer = window.posterEditor?.elements[WhiteboardDomElement.WHITEBOARD_CONTAINER]?.get(0);
      if (!whiteboardContainer) return;

      const rect = whiteboardContainer.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      const scaleRatio = newScale / currentScale;
      const newScrollLeft = (whiteboardContainer.scrollLeft + mouseX) * scaleRatio - mouseX;
      const newScrollTop = (whiteboardContainer.scrollTop + mouseY) * scaleRatio - mouseY;

      this.setZoom(newScale);
      this.wheelZoomGestureTracker.onWheelZoom(currentScale, newScale);
      whiteboardContainer.scroll({
        left: newScrollLeft,
        top: newScrollTop,
        behavior: 'instant',
      });
      this.poster.refreshAllVisibleCanvases();
    }
  };

  public zoomIn(): void {
    const data = this.getZoomData();
    const zoomFactor = this.getZoomInStepFromCurrentScale(data.currentScale);

    if (data.bestFitScale > data.currentScale && data.bestFitScale < data.currentScale + zoomFactor) {
      this.fitToScreen();
    } else {
      this.setZoom(data.currentScale + zoomFactor);
    }
  }

  public setZoom(scale: number, animate = false, doRefreshVisibleCanvas = true, duration = 100): void {
    const validatedScale = this.validateNewZoom(scale);

    if (animate) {
      this.animateZoomTransition(validatedScale, duration);
    } else {
      this.applyZoom(validatedScale, doRefreshVisibleCanvas);
    }
  }

  private animateZoomTransition(targetScale: number, duration = 100): void {
    if (this.animateZoomFrameId) {
      window.clearTimeout(this.animateZoomFrameId);
    }

    this.isScaleAnimating = true;
    const startTime = performance.now();
    let currentTime = performance.now();

    const startScale = this.scale;
    this.animatingScaleEndValue = targetScale;
    this.isFitToScreen = targetScale === this.getFitToScreenScale();
    this.updateIsFitToScreenByScale(targetScale);

    const animate = (): void => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      const eased = easeInOutCubic(progress);
      const currentScale = startScale + (targetScale - startScale) * eased;

      for (const [, page] of Object.entries(this.poster.pages.pagesHashMap)) {
        const backdrop = page.canvasBackdrop;
        const newWidth = this.poster.width * currentScale;
        if (backdrop) {
          backdrop.style.width = `${newWidth}px`;
          backdrop.style.height = `${this.poster.height * currentScale}px`;
        }

        page.fabricCanvas.setZoom(currentScale);
        page.refreshVisibleCanvas();
      }

      this.poster.redux.updateReduxData();

      currentTime = performance.now();

      if (progress < 1) {
        this.animateZoomFrameId = window.setTimeout(animate, 16);
      } else {
        this.poster.redux.updateReduxData();
        this.isScaleAnimating = false;
        this.animateZoomFrameId = undefined;
        this.animatingScaleEndValue = undefined;
        this.scrollToPendingPage();
      }
    };

    animate();
  }

  private applyZoom(scale: number, doRefreshVisibleCanvas = true): void {
    this.isFitToScreen = scale === this.getFitToScreenScale();
    this.updateIsFitToScreenByScale(scale);
    this.poster.pages.setZoom(scale, doRefreshVisibleCanvas);
    this.poster.redux.updatePosterScaling();
  }

  public updateIsFitToScreenByScale(scale: number): void {
    this.isFitToScreen = scale === this.getFitToScreenScale();
  }

  private getZoomData(): ZoomData {
    const posterScale = this.scale;

    return {
      bestFit: this.isCurrentScaleBestFit(),
      bestFitScale: this.getFitToScreenScale(),
      currentScale: roundPrecision(posterScale, SCALE_PRECISION),
    };
  }

  public getFitToScreenScale(): number {
    return getFitToScreenScale(this.poster.width, this.poster.height, modeHasBufferForFitToScreen(this.poster.mode.details.type));
  }

  public isCurrentScaleBestFit(leeway?: number): boolean {
    const posterScale = this.scale;
    const LEEWAY = leeway ?? 0.01;

    if (posterScale === undefined) {
      return false;
    }

    return Math.abs(roundPrecision(posterScale, SCALE_PRECISION) - this.getFitToScreenScale()) < LEEWAY;
  }

  private onWindowResize(): void {
    if (this.isDisposed) {
      return;
    }
    if (this.poster.mode.isWebpage()) {
      this.fitToScreen();
    } else {
      this.poster.refreshAllVisibleCanvases();
    }
  }

  public validateNewZoom(val: number): number {
    const minZoom = this.getMinZoom();
    if (val < minZoom) {
      return minZoom;
    }
    if (val > ZOOM_MAX) {
      return ZOOM_MAX;
    }

    return roundPrecision(val, SCALE_PRECISION);
  }

  public getMinZoom(): number {
    if (this.poster.mode.isWebpage()) {
      return this.getFitToScreenScale();
    }

    return Math.min(this.getMinZoomToFitPosterComfortablyOnScreen(), ZOOM_MIN);
  }

  private getMinZoomToFitPosterComfortablyOnScreen(): number {
    return this.getFitToScreenScale() * 0.5;
  }

  private getZoomInStepFromCurrentScale = (currentScale: number): number => {
    let zoomFactor = 0.5;

    if (currentScale >= 0.1 && currentScale < 1.5) {
      const zoomStep = 0.1;
      const distanceFromNextZoomValue = calculateAdjustedModulus(currentScale, zoomStep);

      zoomFactor = zoomStep - distanceFromNextZoomValue;
    }
    if (currentScale >= 1.5 && currentScale < 2.0) {
      const zoomStep = 0.25;
      const distanceFromNextZoomValue = calculateAdjustedModulus(currentScale, zoomStep);

      zoomFactor = zoomStep - distanceFromNextZoomValue;
    }

    return zoomFactor;
  };

  private getZoomOutStepFromCurrentScale = (currentScale: number): number => {
    let zoomFactor = 0.5;

    if (currentScale > 0.1 && currentScale <= 1.5) {
      const zoomStep = 0.1;
      const distanceFromNextZoomValue = calculateAdjustedModulus(currentScale, zoomStep);

      zoomFactor = distanceFromNextZoomValue === 0 ? zoomStep : distanceFromNextZoomValue;
    }
    if (currentScale > 1.5 && currentScale <= 2.0) {
      const zoomStep = 0.25;
      const distanceFromNextZoomValue = calculateAdjustedModulus(currentScale, zoomStep);

      zoomFactor = distanceFromNextZoomValue === 0 ? zoomStep : distanceFromNextZoomValue;
    }

    return zoomFactor;
  };
}

export default PosterScaling;

export const getFitToScreenScale = (canvasWidth: number, canvasHeight: number, hasBuffer = true): number => {
  const buffer = hasBuffer ? getFitToScreenBuffer() : 0;
  const pw = canvasWidth;
  const ph = canvasHeight;

  if (pw === undefined || ph === undefined) {
    throw new Error('Undefined whiteboard container width');
  }

  return getScaleToFit(getAvailableWidthForCanvas() - buffer, getAvailableHeightForCanvas() - buffer, pw, ph);
};

export const getFitToScreenBuffer = (): number => {
  if (isEditorMobileVariant()) {
    return FIT_TO_SCREEN_BUFFER_MOBILE;
  }
  return FIT_TO_SCREEN_BUFFER_DESKTOP;
};

export const modeHasBufferForFitToScreen = (modeType: PosterModeType): boolean => {
  return modeType !== PosterModeType.WEB_PAGE;
};

const getAvailableHeightForCanvas = (): number => {
  const whiteboardContainerHeight = window.posterEditor?.elements[WhiteboardDomElement.WHITEBOARD_CONTAINER]?.height();
  if (whiteboardContainerHeight === undefined) {
    throw new Error('Undefined whitebaord container height');
  }

  const pageSliderHeight = window.posterEditor?.whiteboard?.mode.isWebpage() ? 0 : PAGE_SLIDER_CONTAINER_HEIGHT;
  return whiteboardContainerHeight - pageSliderHeight - (window.posterEditor?.elements[WhiteboardDomElement.POST_POSTER_ELEMENT]?.height() ?? 0);
};

const getAvailableWidthForCanvas = (): number => {
  const whiteboardContainerElement = window.posterEditor?.elements[WhiteboardDomElement.WHITEBOARD_CONTAINER]?.get(0);
  if (!whiteboardContainerElement) {
    throw new Error('Undefined whiteboard container element');
  }

  const computedStyle = window.getComputedStyle(whiteboardContainerElement);
  const paddingLeft = parseFloat(computedStyle.paddingLeft);
  const paddingRight = parseFloat(computedStyle.paddingRight);
  const widthWithoutPadding = whiteboardContainerElement.clientWidth - paddingLeft - paddingRight;

  const isWebpageMode = window.posterEditor?.whiteboard?.mode.isWebpage();
  return widthWithoutPadding - (isEditorMobileVariant() || isWebpageMode ? 0 : PAGE_OPTIONS_WIDTH);
};
